import { Link, useParams, Navigate } from "react-router-dom";
import { SPACING } from '@/design-system';
import { cn } from '@/lib/utils';
import { LPages, LCommon } from "@/components/i18n";
import { useLocalization } from "@/hooks/useLocalization";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SEOHead } from "@/components/seo/SEOHead";
import { Home, Users, FileText, HelpCircle, MapPinOff, FolderX, FileX2, UserX } from "lucide-react";


export type NotFoundEntityType = 'page' | 'topic' | 'publication' | 'expert';

interface NotFoundProps {
  entityType?: NotFoundEntityType;
  backLink?: string;
  backLabel?: string;
}

const ENTITY_CONFIG = {
  page: {
    icon: MapPinOff,
    titleKey: 'not_found.title',
    messageKey: 'not_found.message',
    titleDefaultValue: 'Page Not Found',
    messageDefaultValue: 'The page you are looking for does not exist or has been removed.',
  },
  topic: {
    icon: FolderX,
    titleKey: 'topics.not_found.title',
    messageKey: 'topics.not_found.description',
    titleDefaultValue: 'Topic Not Found',
    messageDefaultValue: 'The topic you requested could not be found. It may have been moved or removed.',
  },
  publication: {
    icon: FileX2,
    titleKey: 'publications.not_found.title',
    messageKey: 'publications.not_found.description',
    titleDefaultValue: 'Publication Not Found',
    messageDefaultValue: 'The publication you are looking for does not exist or has been removed.',
  },
  expert: {
    icon: UserX,
    titleKey: 'expert_profile.not_found.title',
    messageKey: 'expert_profile.not_found.description',
    titleDefaultValue: 'Expert Not Found',
    messageDefaultValue: 'The requested expert profile does not exist or was removed.',
  },
};

const NotFound = ({ entityType = 'page', backLink, backLabel }: NotFoundProps) => {
  const { segment1 } = useParams();
  const { locationSlug, getPath } = useLocalization();
  
  const config = ENTITY_CONFIG[entityType];
  const IconComponent = config.icon;
  
  // Use location slug from URL or context for location-aware home links
  const homeLocationSlug = segment1 || locationSlug;
  const homeLink = homeLocationSlug ? `/${homeLocationSlug}` : '/';
  
  // Global app routes use getPath (smart detection - no location prefix)
  const expertsLink = getPath('experts');
  const insightsLink = getPath('insights');

  // Determine back link based on entity type if not provided
  const resolvedBackLink = backLink || (entityType === 'expert' ? expertsLink : entityType === 'publication' ? insightsLink : homeLink);
  const resolvedBackLabel = backLabel || (entityType === 'expert' ? 'actions.back_to_experts' : entityType === 'publication' ? 'actions.back_to_insights' : 'actions.return_home');

  return (
    <>
      <SEOHead
        title={config.titleDefaultValue}
        description={config.messageDefaultValue}
        noindex
      />
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-background to-muted px-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className={cn(SPACING.pageContainer, 'pt-8 pb-8')}>
            {/* Icon */}
            <div className="flex justify-center">
              <div className={cn(SPACING.cardPadding, 'rounded-full bg-muted')}>
                <IconComponent className="h-12 w-12 text-muted-foreground" />
              </div>
            </div>
            
            {/* Stylized 404 */}
            <h1 className="text-8xl font-bold bg-gradient-to-r from-primary to-primary/50 bg-clip-text text-transparent">
              404
            </h1>
            
            {/* Title & Message */}
            <div className={cn(SPACING.listItemGap)}>
              <h2 className="text-2xl font-semibold">
                <LPages id={config.titleKey} defaultValue={config.titleDefaultValue} />
              </h2>
              <p className="text-muted-foreground">
                <LPages id={config.messageKey} defaultValue={config.messageDefaultValue} />
              </p>
            </div>
            
            {/* Primary CTA */}
            <Button asChild size="lg" className="w-full">
              <Link to={resolvedBackLink}>
                <Home className="h-4 w-4 mr-2" />
                <LCommon id={resolvedBackLabel} defaultValue='Return to Home' />
              </Link>
            </Button>
            
            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">
                  <LPages id="not_found.helpful_links" defaultValue='Helpful Links' />
                </span>
              </div>
            </div>
            
            {/* Quick Links */}
            <div className="flex flex-wrap justify-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to={expertsLink}>
                  <Users className="h-4 w-4 mr-1" />
                  <LCommon id="nav.experts" defaultValue='Experts' />
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to={insightsLink}>
                  <FileText className="h-4 w-4 mr-1" />
                  <LCommon id="nav.insights" defaultValue='Insights' />
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to={getPath('help')}>
                  <HelpCircle className="h-4 w-4 mr-1" />
                  <LCommon id="nav.help_center" defaultValue='Help Center' />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default NotFound;
